import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ConversationRef, MessageSubscription } from 'talkjs/all';
import {
  CALL_OUT_OF_JURISDICTION,
  CALL_REROUTED,
} from '@rsos/constants/collaborator';
import useTF911LoggedInUser from '@rsos/hooks/talkjs/useTF911LoggedInUser';
import {
  setCollaboratorIsUnread,
  StateWithEmergencyCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { addTF911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { getTalkInstances, useTalkJS } from '@rsos/utils/useTalkJS';
import useTalkJSTheme from '../../../../hooks/useTalkJSTheme';
import { useChatContext } from '../ChatContext';
import { ChatContainer } from './CollaboratorChatbox.styles';

const CollaboratorChat = () => {
  const { isCollapsed } = useChatContext();
  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  if (!callerID) {
    throw new Error('Conversation used outside of selected caller context');
  }

  const collaborator = useSelector(
    (state: StateWithEmergencyCalls) =>
      state.emergencyCalls.callQueue[callerID].collaborator,
  );

  const collaboratorType = collaborator.type;

  const conversationID = collaborator.conversationID;

  const tf911Conversation = useSelectedTextFrom911Conversation(callerID);
  const tf911ConversationID = tf911Conversation?.data?.conversation_id;
  const tf911CustomStatus = tf911Conversation?.data?.custom?.STATUS;

  const dispatch = useDispatch();
  const [convoReference, setConvoReference] = useState<ConversationRef | null>(
    null,
  );
  const chatTheme = useTalkJSTheme(true);
  const loggedInUser = useTF911LoggedInUser();

  const { shellRef, isLoaded } = useTalkJS({
    theme: chatTheme,
    user: loggedInUser,
    instanceID: 'collaborator',
  });

  const { chat, session } = getTalkInstances(chatTheme, 'collaborator');

  const getConvoReferenceCustom = useCallback(async () => {
    if (convoReference) {
      const conversationSnapshot = await convoReference.get();

      return conversationSnapshot?.custom;
    }

    return undefined;
  }, [convoReference]);

  // Set Conversation
  useEffect(() => {
    const setupConversation = async () => {
      if (chat && session && conversationID && isLoaded) {
        const conversationRef = session.conversation(conversationID);
        await conversationRef.set({}); // User must be added to conversation before selecting
        setConvoReference(conversationRef);

        await chat.select(conversationID);
      }
    };

    setupConversation();
  }, [session, chat, conversationID, loggedInUser, callerID, isLoaded]);

  // Send a message when TF911 is initiated
  // Can be simplified once subscribe() is implemented
  useEffect(() => {
    const setupAndSend = async () => {
      const customAttributes = await getConvoReferenceCustom();

      const customTF911ID = customAttributes?.tf911ConversationID;
      const customStatus = customAttributes?.tf911CustomStatus;

      const isStatusDifferent = customStatus !== tf911CustomStatus;
      const isConversationDifferent = customTF911ID !== tf911ConversationID;

      // Receiving ECC reads conversation custom attribute and sees misaligned TF911 ConversationID
      if (
        convoReference &&
        customTF911ID &&
        isConversationDifferent &&
        collaboratorType === CALL_OUT_OF_JURISDICTION
      ) {
        dispatch(
          addTF911Conversation({
            callerID,
            data: {
              conversation_id: customTF911ID,
              custom: {
                STATUS: customStatus,
              },
            },
          }),
        );
      }
      // Jurisdictional ECC updated TF911 ConversationID and updates conversation custom attribute
      else if (
        convoReference &&
        tf911ConversationID &&
        (isConversationDifferent ||
          (!isConversationDifferent && isStatusDifferent)) &&
        collaboratorType === CALL_REROUTED
      ) {
        const tf911Attributes = {
          tf911ConversationID: tf911ConversationID,
          tf911CustomStatus: tf911CustomStatus,
        };

        convoReference.set({
          custom: { ...tf911Attributes },
        });
        convoReference.send({
          text: 'TF911 Chat Updated',
          custom: { ...tf911Attributes },
        });
      }
    };

    setupAndSend();
  }, [
    convoReference,
    tf911ConversationID,
    getConvoReferenceCustom,
    callerID,
    dispatch,
    collaboratorType,
    tf911CustomStatus,
  ]);

  // Receiving ECC subscribes to Messages - Need TalkJS Image upgrade to use subscribe()
  useEffect(() => {
    let subscription: MessageSubscription;
    const subscribeAndUnsubscribe = async () => {
      if (convoReference && collaboratorType === CALL_OUT_OF_JURISDICTION) {
        subscription = convoReference.subscribeMessages(messages => {
          const latestMessageConversationID =
            messages?.[0]?.custom?.tf911ConversationID;

          if (
            messages &&
            messages.length > 0 &&
            latestMessageConversationID &&
            latestMessageConversationID !== tf911ConversationID
          ) {
            dispatch(
              addTF911Conversation({
                callerID,
                data: {
                  conversation_id: latestMessageConversationID,
                },
              }),
            );
          }
        });
      }
    };

    subscribeAndUnsubscribe();

    return () => {
      subscription?.unsubscribe();
    };
  }, [
    convoReference,
    tf911ConversationID,
    callerID,
    dispatch,
    collaboratorType,
  ]);

  // Mark chat as read
  useEffect(() => {
    if (collaborator.isUnread) {
      dispatch(setCollaboratorIsUnread({ callerID, isUnread: false }));
    }
  }, [collaborator.isUnread, callerID, dispatch]);

  return (
    <>
      <ChatContainer ref={shellRef} display={isCollapsed ? 'none' : 'flex'} />
    </>
  );
};

export default CollaboratorChat;
